import React, { useState } from "react";
import { Flex, List, ListItem, Portal } from "@chakra-ui/react";
import { AnimatePresence } from "framer-motion";
import { useColorTheme } from "contexts/color-theme";
import { ChakraBox } from "ui/chakra-framer";
import { type NavTabProps, type SubNavItemProps } from "./data";
import { NavLink } from "./nav-link";
import { Popover } from "./popover";

type DesktopNavProps = {
  tabs: NavTabProps[];
  popoverWidth?: string;
};

export function DesktopNav({ tabs, popoverWidth }: DesktopNavProps) {
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<SubNavItemProps["label"]>();
  const { theme } = useColorTheme();

  const resetActiveTab = () => {
    setActiveTab(undefined);
    setPopoverOpen(false);
  };

  return (
    <Flex
      as="nav"
      aria-label="Desktop navigation."
      display={{ base: "none", lg: "flex" }}
      align="center"
      flexGrow={1}
      justify="center"
    >
      <List
        display="flex"
        alignItems="center"
        listStyleType="none"
        pl={0} // Remove default padding
        onMouseLeave={resetActiveTab}
      >
        {tabs.map((navItem) => {
          const isActive = navItem.label === activeTab;
          const isDimmed = Boolean(activeTab) && navItem.label !== activeTab;

          return (
            <ListItem key={navItem.label} display="flex" alignItems="center">
              {navItem.href ? (
                <NavLink
                  isActive={isActive}
                  isDimmed={isDimmed}
                  href={navItem.href}
                  onMouseEnter={() => {
                    setPopoverOpen(false);
                    setActiveTab(navItem.label);
                  }}
                  onFocus={() => {
                    setPopoverOpen(false);
                    setActiveTab(navItem.label);
                  }}
                  onMouseLeave={resetActiveTab}
                  onFocusOut={resetActiveTab}
                >
                  {navItem.label}
                </NavLink>
              ) : (
                <Popover
                  isActive={isActive}
                  isDimmed={isDimmed}
                  navItem={navItem}
                  contentWidth={popoverWidth}
                  onOpen={() => {
                    setActiveTab(navItem.label);
                    setPopoverOpen(true);
                  }}
                />
              )}
            </ListItem>
          );
        })}
      </List>

      <Portal>
        {popoverOpen && (
          <AnimatePresence>
            <ChakraBox
              bg={theme === "light" ? "blackAlpha.700" : "blackAlpha.800"}
              backdropFilter="blur(8px)"
              pos="absolute"
              inset={0}
              zIndex="dropdown"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { duration: 0.15 } }} // Animation avoids a flicker while the dropdown is opening
              onMouseEnter={resetActiveTab}
            />
          </AnimatePresence>
        )}
      </Portal>
    </Flex>
  );
}
